import { useEffect, useState } from 'react'
import { api, type Staff } from '../lib/api'
import { money } from '../lib/format'
import { Panel, Alert, Loading, Empty, Tag } from '../components/ui'

type Resource = 'writers' | 'clients' | 'staff' | 'job-types'

/** 一頁幾筆；超過才會出現分頁列 */
const PAGE_SIZE = 15

/**
 * cols     = 編輯表單的欄位（完整的一組，少了就沒地方可以編輯）
 * listCols = 清單表格要顯示哪幾欄；沒指定就跟 cols 一樣
 * 兩者分開，才能讓清單只留重點欄位，又不影響編輯功能
 */
const TABS: Array<{
  key: Resource
  label: string
  /**
   * span＝在 12 欄表單裡佔幾格；不填就是 3（一列四欄，跟原本一樣）
   * side＝放到右側區塊（要打長文字的欄位），不進左邊那組格線
   */
  cols: Array<{
    k: string; t: string; type?: string; num?: boolean; options?: string[]
    multiline?: boolean; span?: number; side?: boolean; rows?: number; grow?: boolean
    /** 選項來自人員主檔（存 staff.id）；只有 staff_id 在用 */
    staffRef?: boolean
    /** 只有在這個條件成立時才顯示；不填就一律顯示 */
    showIf?: (draft: Record<string, any>) => boolean
  }>
  listCols?: string[]
}> = [
  {
    key: 'writers',
    label: '寫手名冊',
    listCols: [
      'status', 'lang', 'name', 'name_en', 'kind', 'staff_id',
      'signed_on', 'expires_on', 'default_price', 'monthly_max',
      'email', 'review_folder', 'contract_codes', 'note',
    ],
    /*
     * 表單分兩區並排：
     *   左（A）狀態到驗收資料夾，自己一組 12 欄格線
     *          狀態3 類型3 語系3 姓名3
     *          英文名3 簽約日3 到期日3 單價3
     *          月篇數上限3 Email6 對應員工3（只有內部發包看得到，其餘留白）
     *          驗收資料夾12
     *   右（B）歷次合約編號，底下接備註 —— 都是要打長文字的欄位
     */
    cols: [
      { k: 'status', t: '狀態', span: 3, options: ['試寫中', '合作中', '停止合作', '備用'] },
      { k: 'kind', t: '類型', span: 3, options: ['個人', '廠商', '內部發包'] },
      { k: 'lang', t: '語系', span: 3, options: ['中', '英'] },
      { k: 'name', t: '姓名', span: 3 },
      { k: 'name_en', t: '英文名', span: 3 },
      { k: 'signed_on', t: '簽約日', type: 'date', span: 3 },
      { k: 'expires_on', t: '到期日', type: 'date', span: 3 },
      { k: 'default_price', t: '單價', type: 'number', num: true, span: 3 },
      { k: 'monthly_max', t: '月篇數上限', type: 'number', num: true, span: 3 },
      { k: 'email', t: 'Email', span: 6 },
      // 內包案件的驗收人不得是承包人本人，系統要靠這個對應才認得出「寫手＝員工」
      {
        k: 'staff_id', t: '對應員工（內包驗收用）', span: 3, staffRef: true,
        showIf: (d) => d.kind === '內部發包',
      },
      { k: 'review_folder', t: '驗收資料夾', span: 12 },
      // 編號規則還在規劃中，先自由輸入、一行一個；日後要拆成子表可以逐行搬過去
      { k: 'contract_codes', t: '歷次合約編號（一行一個）', multiline: true, side: true, grow: true },
      { k: 'note', t: '備註', multiline: true, side: true, rows: 4 },
    ],
  },
  { key: 'clients', label: '客戶', cols: [{ k: 'name', t: '客戶名稱' }, { k: 'short_name', t: '簡稱' }, { k: 'note', t: '備註' }] },
  { key: 'staff', label: '人員', cols: [{ k: 'name', t: '姓名' }, { k: 'email', t: 'Email' }, { k: 'roles', t: '角色（逗號分隔）' }] },
  { key: 'job-types', label: '案件類型', cols: [{ k: 'name', t: '名稱' }, { k: 'lang', t: '語系' }, { k: 'default_price', t: '預設單價', type: 'number', num: true }] },
]

/**
 * 新增表單的初始值：把每個下拉選單的第一個選項先寫進表單狀態。
 *
 * 不能只靠 <select value={draft[k] ?? options[0]}> 讓畫面顯示第一個選項——
 * 那樣使用者沒動過的下拉根本不會出現在送出的 payload 裡，
 * 後端的 clean() 會跳過它，NOT NULL 又沒有預設值的欄位（writers.lang）
 * 就會被資料庫直接擋下來。畫面顯示什麼，就要真的送出什麼。
 */
function blankDraft(spec: (typeof TABS)[number]): Record<string, any> {
  const out: Record<string, any> = {}
  for (const c of spec.cols) if (c.options) out[c.k] = c.options[0]
  return out
}

export default function Masters() {
  const [tab, setTab] = useState<Resource>('writers')
  const [rows, setRows] = useState<any[] | null>(null)
  const [error, setError] = useState('')
  const [draft, setDraft] = useState<Record<string, any>>({})
  const [editingId, setEditingId] = useState<number | null>(null)
  const [keyword, setKeyword] = useState('')
  const [lang, setLang] = useState('')   // '' = 全部；'中' / '英'
  const [page, setPage] = useState(0)

  /** 人員名冊，給「對應員工」下拉用；只有寫手頁在用，載一次就好 */
  const [staffList, setStaffList] = useState<Staff[]>([])

  const spec = TABS.find((t) => t.key === tab)!
  const mainCols = spec.cols.filter((c) => !c.side && (!c.showIf || c.showIf(draft)))
  const sideCols = spec.cols.filter((c) => c.side)
  // 依 listCols 指定的順序取欄位；沒設定就整組拿來用
  const listCols = spec.listCols
    ? spec.listCols.map((k) => spec.cols.find((c) => c.k === k)!).filter(Boolean)
    : spec.cols

  /**
   * 搜尋與分頁都在前端做：主檔筆數不多（寫手 29、客戶 15），
   * 一次載入後即時篩選比每打一個字就送一次請求順手。
   */
  /** 這個主檔有沒有語系欄位（寫手、案件類型有；客戶、人員沒有） */
  const hasLang = spec.cols.some((c) => c.k === 'lang')
  const filtered = (rows ?? []).filter((r) => {
    if (lang && String(r.lang ?? '') !== lang) return false
    const kw = keyword.trim().toLowerCase()
    if (!kw) return true
    return listCols.some((c) => String(r[c.k] ?? '').toLowerCase().includes(kw))
  })
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)
  const paged = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE)

  const load = () => {
    setRows(null)
    api.get<any[]>(`/masters/${tab}`).then(setRows).catch((e) => setError(e.message))
  }

  useEffect(() => {
    setDraft(blankDraft(spec))
    setEditingId(null)
    setKeyword('')
    setLang('')
    setPage(0)
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  // 換篩選條件時回到第一頁，否則會停在一個已經不存在的頁碼上
  useEffect(() => { setPage(0) }, [keyword, lang])

  useEffect(() => {
    api.get<Staff[]>('/masters/staff').then(setStaffList).catch(() => {})
  }, [])

  const save = async () => {
    setError('')
    // 類型從「內部發包」改成外部後，殘留的對應員工要一起清掉，
    // 否則驗收人下拉會莫名其妙少一個人
    const payload =
      tab === 'writers' && draft.kind !== '內部發包' && draft.staff_id != null
        ? { ...draft, staff_id: null }
        : draft
    try {
      if (editingId) await api.put(`/masters/${tab}/${editingId}`, payload)
      else await api.post(`/masters/${tab}`, payload)
      setDraft(blankDraft(spec))
      setEditingId(null)
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  const remove = async (id: number) => {
    if (!confirm('確定要停用這筆資料？（不會實際刪除，以免影響既有單據）')) return
    try {
      await api.del(`/masters/${tab}/${id}`)
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }


  return (
    <>
      <div className="page-head">
        <h2>主檔維護</h2>
        <div className="btn-row">
          {TABS.map((t) => (
            <button key={t.key} className={t.key === tab ? 'primary' : ''} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <Alert kind="error">{error}</Alert>

      <Panel title={editingId ? `編輯 #${editingId}` : `新增${spec.label}`}>
        {/*
          左區是一般欄位的 12 欄格線，右區放要打長文字的欄位（合約編號、備註）。
          沒有 side 欄位的主檔（客戶、人員、案件類型）不會長出右區，版面同原本。
        */}
        <div
          style={
            sideCols.length
              ? { display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 20, alignItems: 'stretch' }
              : undefined
          }
        >
          <div className="grid c12">
            {mainCols.map((c) => (
              <label className={`field s${c.span ?? 3}`} key={c.k}>
                <span>{c.t}</span>
                {c.staffRef ? (
                  <select
                    value={draft[c.k] ?? ''}
                    onChange={(e) => setDraft({ ...draft, [c.k]: Number(e.target.value) || null })}
                  >
                    <option value="">— 未對應 —</option>
                    {staffList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                ) : c.options ? (
                  <select
                    value={draft[c.k] ?? c.options[0]}
                    onChange={(e) => setDraft({ ...draft, [c.k]: e.target.value })}
                  >
                    {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input
                    type={c.type ?? 'text'}
                    value={draft[c.k] ?? ''}
                    onChange={(e) =>
                      setDraft({ ...draft, [c.k]: c.type === 'number' ? Number(e.target.value) : e.target.value })
                    }
                  />
                )}
              </label>
            ))}
          </div>

          {sideCols.length > 0 && (
            // grow 的欄位吃掉剩餘高度，下面那格（備註）就會跟左區最後一列（驗收資料夾）切齊
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateRows: sideCols.map((c) => (c.grow ? '1fr' : 'auto')).join(' '),
              }}
            >
              {sideCols.map((c) => (
                <label
                  className="field"
                  key={c.k}
                  style={c.grow ? { display: 'flex', flexDirection: 'column', minHeight: 0 } : undefined}
                >
                  <span>{c.t}</span>
                  <textarea
                    rows={c.rows ?? 4}
                    style={c.grow ? { flex: 1, minHeight: 96 } : undefined}
                    placeholder={c.k === 'contract_codes' ? '一行一個編號' : undefined}
                    value={draft[c.k] ?? ''}
                    onChange={(e) => setDraft({ ...draft, [c.k]: e.target.value })}
                  />
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="btn-row" style={{ marginTop: 12 }}>
          <button className="primary" onClick={save}>{editingId ? '儲存變更' : '新增'}</button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setDraft(blankDraft(spec)) }}>取消</button>
          )}
        </div>
      </Panel>

      <Panel
        title={`${spec.label}清單`}
        actions={
          <div className="btn-row">
            {/* 語系篩選：再按一次同一顆就回到全部 */}
            {hasLang && ['中', '英'].map((L) => (
              <button
                key={L}
                className={lang === L ? 'primary' : undefined}
                aria-pressed={lang === L}
                title={lang === L ? '再按一次顯示全部' : `只看${L}文`}
                onClick={() => setLang(lang === L ? '' : L)}
                style={{ minWidth: 44 }}
              >
                {L}
              </button>
            ))}
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={`搜尋${spec.label}…`}
              style={{ width: 200 }}
            />
            {(keyword || lang) && (
              <button onClick={() => { setKeyword(''); setLang('') }}>清除</button>
            )}
            <span className="hint" style={{ fontSize: 12, color: 'var(--ink-soft)', whiteSpace: 'nowrap' }}>
              {keyword || lang
                ? `符合 ${filtered.length} / ${rows?.length ?? 0} 筆`
                : `共 ${rows?.length ?? 0} 筆`}
            </span>
          </div>
        }
      >
        {!rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty />
        ) : filtered.length === 0 ? (
          <Empty>
            找不到{lang && `語系為「${lang}」`}
            {keyword && `${lang ? '且' : ''}符合「${keyword}」`}的資料
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  {/* 欄位一律不換行，寬度不夠時整張表橫向捲動（外層 .table-wrap） */}
                  {listCols.map((c) => (
                    <th key={c.k} className={c.num ? 'num' : ''} style={{ whiteSpace: 'nowrap' }}>{c.t}</th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.id}>
                    {listCols.map((c) => (
                      <td key={c.k} className={c.num ? 'num' : ''} style={{ whiteSpace: 'nowrap' }}>
                        {c.staffRef ? (staffList.find((s) => s.id === r[c.k])?.name ?? '')
                          : c.k === 'status' ? <Tag value={r[c.k]} />
                          : c.num ? money(r[c.k])
                          // 多行欄位在清單裡收成一行，換行改用頓號分隔
                          : c.multiline ? String(r[c.k] ?? '').split('\n').map((x) => x.trim()).filter(Boolean).join('、')
                          : String(r[c.k] ?? '')}
                      </td>
                    ))}
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div className="btn-row" style={{ flexWrap: 'nowrap' }}>
                        <button onClick={() => { setEditingId(r.id); setDraft(r) }}>編輯</button>
                        <button className="danger" onClick={() => remove(r.id)}>停用</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 只有超過一頁時才出現分頁列，資料少的時候不佔版面 */}
            {totalPages > 1 && (
              <div
                className="btn-row"
                style={{ marginTop: 12, justifyContent: 'flex-end', alignItems: 'center' }}
              >
                <span style={{ fontSize: 12, color: 'var(--ink-soft)', marginRight: 'auto' }}>
                  第 {safePage * PAGE_SIZE + 1}–{safePage * PAGE_SIZE + paged.length} 筆，共 {filtered.length} 筆
                </span>
                <button disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>上一頁</button>
                <span style={{ fontSize: 13, minWidth: 64, textAlign: 'center' }}>
                  {safePage + 1} / {totalPages}
                </span>
                <button disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)}>下一頁</button>
              </div>
            )}
          </div>
        )}
      </Panel>
    </>
  )
}
